import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { AssessmentState, Assessment, AssessmentSubmission } from '../../types'
import * as assessmentAPI from '../../api/assessments'

const initialState: AssessmentState = {
  assessments: [],
  currentAssessment: null,
  submissions: [],
  currentSubmission: null,
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchAssessments = createAsyncThunk(
  'assessments/fetchAssessments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await assessmentAPI.getAssessments()
      return response.data
    } catch (error: any) {
      console.error('❌ Assessment slice: Error fetching:', error)
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assessments')
    }
  }
)

export const fetchAssessmentById = createAsyncThunk(
  'assessments/fetchAssessmentById',
  async (assessmentId: string, { rejectWithValue }) => {
    try {
      const response = await assessmentAPI.getAssessmentById(assessmentId)
      return response.data || response // Handle both wrapped and direct responses
    } catch (error: any) {
      console.error('❌ Assessment slice: Error fetching assessment:', error)
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assessment')
    }
  }
)

export const startAssessment = createAsyncThunk(
  'assessments/startAssessment',
  async (assessmentId: string, { rejectWithValue }) => {
    try {
      const response = await assessmentAPI.startAssessment(assessmentId)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start assessment')
    }
  }
)

export const submitAssessment = createAsyncThunk(
  'assessments/submitAssessment',
  async ({ assessmentId, answers }: { assessmentId: string; answers: any[] }, { rejectWithValue }) => {
    try {
      const response = await assessmentAPI.submitAssessment(assessmentId, answers)
      // The API now returns the submission object directly
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit assessment')
    }
  }
)

const assessmentSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentAssessment: (state) => {
      state.currentAssessment = null
      state.currentSubmission = null
    },
    setCurrentAssessment: (state, action: PayloadAction<Assessment | null>) => {
      state.currentAssessment = action.payload
    },
    setCurrentSubmission: (state, action: PayloadAction<AssessmentSubmission | null>) => {
      state.currentSubmission = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch Assessments
    builder
      .addCase(fetchAssessments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssessments.fulfilled, (state, action) => {
        state.isLoading = false
        state.assessments = action.payload
        state.error = null
      })
      .addCase(fetchAssessments.rejected, (state, action) => {
        console.error('❌ Assessment reducer: REJECTED with error:', action.payload)
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch Assessment By ID
    builder
      .addCase(fetchAssessmentById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssessmentById.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentAssessment = action.payload
        state.error = null
      })
      .addCase(fetchAssessmentById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Start Assessment
    builder
      .addCase(startAssessment.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(startAssessment.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentSubmission = action.payload
        state.error = null
      })
      .addCase(startAssessment.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Submit Assessment
    builder
      .addCase(submitAssessment.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(submitAssessment.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentSubmission = action.payload
        state.submissions.push(action.payload)
        state.error = null
      })
      .addCase(submitAssessment.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, clearCurrentAssessment, setCurrentAssessment, setCurrentSubmission } = assessmentSlice.actions
export default assessmentSlice.reducer
